import { Notify, Screen } from "quasar";
import { type Ref, computed, inject, ref } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import type { PopperProps } from "shared/components/base/Popper.vue";
import useStreamFiltering from "shared/composables/useStreamFiltering";
import { isAbortError } from "shared/services/api/isAbortError";
import { matchingSyndications } from "shared/services/fetching/matchingSyndications";
import { type Nullable, type Stream } from "shared/types";
import { type MentionWithSyndication } from "shared/types/mentions";

// eslint-disable-next-line import/prefer-default-export
export function useSyndicationsPopper<
  M extends MentionWithSyndication,
  S = M["syndications"][number],
>({
  mention,
  stream,
  popperAttrs: attrs = {},
  abortController,
}: {
  mention: M;
  stream: Stream;
  popperAttrs?: Partial<PopperProps>;
  abortController?: Ref<Nullable<AbortController>>;
}) {
  const isMobile = inject("isMobile");
  const isLoading = ref(false);
  const { filters, filtering } = useStreamFiltering({ stream });
  const syndicationsMatchingStream = ref<Record<string, boolean> | null>(null);

  const popperAttrs = computed<PopperProps>(() => ({
    "click-outside-capture": true,
    disableClickOutside: false,
    fallbackPlacements: ["left"],
    flip: true,
    offset: [0, isMobile ? 0 : 32],
    placement: isMobile ? "bottom" : "right",
    tether: true,
    width: isMobile ? Math.min(Screen.width, 375) : 350,
    ...attrs,
  }));

  const loadSyndicationMatches = async (): Promise<void> => {
    try {
      if (!isLoading.value && stream?.id && !syndicationsMatchingStream.value) {
        isLoading.value = true;

        syndicationsMatchingStream.value = await matchingSyndications({
          filters: filtering.value ? filters.value.requestFilters() : {},
          mentionId: mention.id,
          mentionType: mention.type,
          options: { signal: abortController?.value?.signal },
          streamId: stream.id,
        });
      }
    } catch (error) {
      if (!isAbortError(error)) {
        Notify.create(
          getLocaleText("syndications.unable_to_load_syndications")
        );
      }
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    syndications: (mention.syndications ?? []) as S[],
    loadSyndicationMatches,
    syndicationsMatchingStream,
    popperAttrs,
  };
}
